// src/oklch.ts
var OKLCH_PATTERN = /^oklch\(\s*([\d.]+)%?\s+([\d.]+)\s+([\d.]+)(?:deg)?\s*\)$/i;
function parseOklch(value) {
  const match = OKLCH_PATTERN.exec(value.trim());
  if (!match) {
    throw new Error(
      `Expected an oklch() colour such as oklch(0.53 0.19 12), received ${JSON.stringify(value)}`
    );
  }
  const raw = Number(match[1]);
  return {
    // Accept both `oklch(53% ...)` and `oklch(0.53 ...)`.
    l: value.includes("%") ? raw / 100 : raw,
    c: Number(match[2]),
    h: Number(match[3])
  };
}
function encode(channel) {
  const v = channel <= 31308e-7 ? channel * 12.92 : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055;
  return Math.round(Math.min(1, Math.max(0, v)) * 255);
}
function oklchToRgb({ l, c, h }) {
  const hRad = h * Math.PI / 180;
  const a = c * Math.cos(hRad);
  const b = c * Math.sin(hRad);
  const lp = l + 0.3963377774 * a + 0.2158037573 * b;
  const mp = l - 0.1055613458 * a - 0.0638541728 * b;
  const sp = l - 0.0894841775 * a - 1.291485548 * b;
  const lc = lp * lp * lp;
  const mc = mp * mp * mp;
  const sc = sp * sp * sp;
  return {
    r: encode(4.0767416621 * lc - 3.3077115913 * mc + 0.2309699292 * sc),
    g: encode(-1.2684380046 * lc + 2.6097574011 * mc - 0.3413193965 * sc),
    b: encode(-0.0041960863 * lc - 0.7034186147 * mc + 1.707614701 * sc)
  };
}

// src/contrast.ts
var HEX_LONG = /^#([0-9a-f]{6})$/i;
var HEX_SHORT = /^#([0-9a-f]{3})$/i;
function hexToRgb(hex) {
  const short = HEX_SHORT.exec(hex);
  if (short) {
    const [r, g, b] = short[1].split("");
    return {
      r: parseInt(r + r, 16),
      g: parseInt(g + g, 16),
      b: parseInt(b + b, 16)
    };
  }
  const long = HEX_LONG.exec(hex);
  if (!long) {
    throw new Error(
      `Expected a hex colour such as #240064 or #fff, received ${JSON.stringify(hex)}`
    );
  }
  const value = parseInt(long[1], 16);
  return {
    r: value >> 16 & 255,
    g: value >> 8 & 255,
    b: value & 255
  };
}
function linearise(channel) {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
function toRgb(value) {
  if (value.trim().toLowerCase().startsWith("oklch(")) {
    return oklchToRgb(parseOklch(value));
  }
  return hexToRgb(value);
}
function relativeLuminance(value) {
  const { r, g, b } = toRgb(value);
  return 0.2126 * linearise(r) + 0.7152 * linearise(g) + 0.0722 * linearise(b);
}
function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}
function meetsAA(foreground, background, use = "text") {
  const required = use === "text" ? 4.5 : 3;
  return contrastRatio(foreground, background) >= required;
}

// src/palette.ts
var palette = {
  white: "oklch(1 0 89.8756)",
  // Purple-tinted neutrals, light end.
  //
  // lilac50/75/150 are also the light canvas gradient (OR spec section 5): the
  // canvas is deliberately tinted rather than near-white, so that white can
  // mean *state* -- a control at rest, a hovered row, a floating overlay --
  // and read as raised at every scroll position. lilac50 doubles as the solid
  // canvas fallback and the top stop.
  lilac50: "oklch(0.975 0.0054 297.7267)",
  /** The light canvas gradient's midpoint. */
  lilac75: "oklch(0.9597 0.0111 303.1115)",
  lilac100: "oklch(0.9474 0.0098 305.4019)",
  /** The light canvas gradient's foot, and the light sunken well. */
  lilac150: "oklch(0.9423 0.0137 299.7933)",
  /** Kept for the sequential chart ramp; the divider moved to lilac225 when
   *  the canvas gained its tint -- see below. */
  lilac200: "oklch(0.9103 0.0167 301.1978)",
  /**
   * Decorative divider. The OR spec darkens the hairline from lilac200 because
   * a #E3DFEB-class rule disappears against the tinted canvas: this measures
   * 1.32:1 on the canvas and 1.19:1 even at the gradient's darkest stop, where
   * lilac200 fell to 1.10:1. See semantic.ts for why ~1.3:1 is fine for a rule
   * and not fine for a control border.
   */
  lilac225: "oklch(0.8862 0.0252 301.0347)",
  lilac300: "oklch(0.7711 0.0493 300.0184)",
  // muted text, dark mode
  /**
   * Navigation text, secondary and muted steps.
   *
   * Both were a step darker until the ambient wash was accounted for. Contrast
   * was gated against the bare panel, but the gradient sits *behind* the text,
   * so what actually renders is the composite -- and the muted step measured
   * 3.77:1 there against the 4.67:1 it appeared to have. These clear 4.5:1 on
   * the panel, the rail, and the washed panel for every pinned accent.
   */
  lilac250: "oklch(0.87 0.042 300)",
  lilac350: "oklch(0.71 0.055 300)",
  /** Control borders. 3.57:1 on white: clears 1.4.11, fails 1.4.3, so it can
   *  outline an input but must never carry text. */
  lilac400: "oklch(0.6313 0.0763 300.7419)",
  lilac500: "oklch(0.5058 0.0753 299.3488)",
  // muted text, AA on white
  // Brand purple. The wordmark is a single colour; there is no secondary.
  /**
   * The dark theme's control border -- the OR spec wants the input outline
   * purple-tinted in dark mode rather than slate, so the form layer carries
   * the brand the way the chrome does. The spec's own #7D63B0 lands at 2.98:1
   * against the raised surface and fails WCAG 1.4.11 there, so this is the
   * same hue and chroma lifted until both surfaces clear 3:1: 3.97:1 on the
   * canvas, 3.25:1 on a card, and still under the 4.5:1 that would make it
   * legal to carry text.
   */
  purple400: "oklch(0.579 0.1186 298.0281)",
  purple500: "oklch(0.5587 0.1839 292.7336)",
  // dark-mode action, see semantic.ts
  purple600: "oklch(0.4453 0.1871 291.8948)",
  purple700: "oklch(0.3875 0.1532 293.962)",
  purple900: "oklch(0.2545 0.1477 286.0312)",
  // brand
  purple950: "oklch(0.2112 0.121 287.9893)",
  // deep: dark canvas, body text on light
  // Dividers inside the brand chrome: white at 12% over #240064 and #14003A.
  purpleBrandDivider: "oklch(0.3378 0.1405 293.5713)",
  purpleBrandDividerDark: "oklch(0.29 0.0895 295.6747)",
  purple975: "oklch(0.1793 0.1021 289.0181)",
  // sunken well, dark; dark nav panel
  /**
   * The dark theme's nav rail. Everything is purple in dark mode, so the rail
   * has to sit a step below the nav panel, which itself sits below the canvas.
   * Luminance compresses hard at this end -- this separates from the panel by
   * 1.06:1, against 1.10:1 for the equivalent light-theme pair.
   */
  purple985: "oklch(0.1 0.0921 289)",
  /**
   * The dark theme's canvas, and the one value in this file that is not
   * purple-tinted.
   *
   * It is the Command Centre's page colour, adopted on preference: the
   * purple-derived canvas it replaces -- purple950, #1B004B -- is the same
   * lightness but three times the chroma, and at full-page scale that reads as
   * a saturated purple field rather than as a surface for data to sit on.
   * Chrome stays purple, so the brand still frames every screen; it is the
   * working canvas that goes quiet.
   *
   * Practically it is a drop-in: L 0.2077 against purple950's 0.2112, so every
   * dark-theme contrast figure measured against the canvas holds, which the
   * gates in __tests__/semantic.test.ts re-measure rather than assume.
   */
  slate900: "oklch(0.2077 0.0398 265.7549)",
  /**
   * The dark theme's raised surface: cards, and every overlay that floats on
   * the canvas -- menus, popovers, tooltips, the account menu at the foot of
   * the rail.
   *
   * The Command Centre's card colour, adopted for the same reason as the
   * canvas beneath it. It also separates better than the purple it replaces:
   * 1.22:1 against the canvas where purple900 managed 1.10:1, so a card reads
   * as lifted without a border having to do the work.
   */
  slate800: "oklch(0.2795 0.0368 260.031)",
  /**
   * A former control border, kept in the ramp. The OR spec moved the dark
   * control border back into the purple family (see purple400); this slate
   * step stays because its band -- clear of 3:1 on both dark surfaces, short
   * of 4.5:1 -- is measured and may be wanted again.
   */
  slate500: "oklch(0.5544 0.0407 257.4166)",
  /**
   * The dark theme's muted text. Slate-native, per the OR spec: "purple-tinted
   * muted stays inside the purple nav". Content muted text follows the slate
   * canvas it sits on -- 6.74:1 there, 5.52:1 on a card, 7.10:1 in the sunken
   * well -- while the nav's own muted steps stay lilac.
   */
  slate400: "oklch(0.7021 0.0455 260.3873)",
  /**
   * The dark theme's decorative hairline. The OR spec asked for a quieter rule
   * than slate700; its own #1A2438 was the class of value this ramp abandoned
   * -- 1.06:1 against a raised card is a menu with no edge -- so this is the
   * darkest slate that still draws on every surface it rules: 1.46:1 on the
   * canvas, 1.20:1 on a card, 1.67:1 on the dark header.
   */
  slate750: "oklch(0.33 0.0392 257.287)",
  /**
   * The dark chart gridline -- a tier below the hairline, present without
   * competing with data. Decorative only.
   */
  slate850: "oklch(0.2453 0.0451 263.2529)",
  /**
   * The top stop of the dark canvas gradient. The dark canvas is lit from
   * above -- a top-to-bottom gradient, not a flat fill -- and this is slate900
   * lifted 0.02 L, the same hue and chroma. Every dark text and status value
   * is re-measured against it, since it is the lightest thing text sits on.
   */
  slate875: "oklch(0.2277 0.0398 265.7549)",
  /**
   * The dark theme's sunken well, and the highlighted row inside an overlay.
   *
   * Authored to sit the same distance below the canvas that the purple well
   * sat below the purple one -- 1.05:1, against the 1.053:1 it replaces -- so
   * the well is exactly as deep as it was and only its hue moved.
   */
  slate950: "oklch(0.18 0.0398 265.7549)",
  // Neutral greys for the not-assessed state. Deliberately untinted, so
  // "we have not scored this" cannot be mistaken for a muted purple label.
  grey500: "oklch(0.5238 0.0283 295.589)",
  grey400: "oklch(0.7278 0.0362 294.6546)",
  // Semantic RAG. Not the neon from the marketing site.
  green600: "oklch(0.51 0.1218 156.5579)",
  green400: "oklch(0.773 0.1754 155.5039)",
  amber700: "oklch(0.5394 0.1562 42.9937)",
  amber400: "oklch(0.8109 0.1634 75.6471)",
  red600: "oklch(0.5003 0.1821 29.5127)",
  red400: "oklch(0.7086 0.1699 26.4707)",
  // Subtle status fills for badges and meter tracks.
  green50: "oklch(0.9788 0.0221 160.2443)",
  amber50: "oklch(0.9849 0.0204 91.5789)",
  red50: "oklch(0.9725 0.0119 23.7032)",
  greenDark: "oklch(0.2706 0.0498 162.0891)",
  amberDark: "oklch(0.2698 0.0451 75.7171)",
  redDark: "oklch(0.2524 0.0595 26.8175)",
  greyDark: "oklch(0.2591 0.05 290.998)",
  /** Chart series 4. Part of the categorical ramp, not a semantic role. */
  chartLilac: "oklch(0.8066 0.0696 296.9714)"
};

// src/semantic.ts
var SEMANTIC_TOKEN_NAMES = [
  // Two surface levels plus a sunken well. Section 3.6: there is no third
  // tier, so nesting is structurally impossible.
  //
  // In light, `surface` is the tinted canvas (its solid fallback -- the
  // gradient itself lives in canvas-from/mid/to below) and `surface-raised` is
  // white. White is a state, not a surface: a control at rest, a hovered or
  // selected row, something that genuinely floats. A white block on the tinted
  // canvas reads as an artefact, which is exactly why the canvas is tinted.
  "surface",
  "surface-raised",
  "surface-sunken",
  /**
   * The canvas gradient's three stops. Both themes light their canvas -- a
   * top-to-bottom gradient rather than a flat fill -- and the `.canvas-gradient`
   * utility in tokens.css composes these. The gradient belongs on the
   * viewport-height scroll container, not the document: on a long page a
   * document-level gradient stretches and the foot goes visibly grey.
   *
   * `surface` stays the solid fallback, equal to the top stop, so sticky
   * chrome (table headers, toolbars) can take a solid background that matches
   * the gradient where they pin.
   */
  "canvas-from",
  "canvas-mid",
  "canvas-to",
  /**
   * Navigation chrome. Section 3.5: "Purple becomes structure, not fill. It
   * carries navigation and chrome. The working canvas stays near-white."
   *
   * Two levels, because NavShell is two-tier: `surface-nav` is the panel and
   * `surface-nav-deep` is the rail behind it. Deep is darker, so the rail reads
   * as the fixed spine and the panel as its content.
   *
   * These are chrome, not content tiers: nothing nests inside them, so the
   * two-surface rule in section 3.6 still holds.
   */
  "surface-nav",
  "surface-nav-deep",
  /**
   * The bar above the content area.
   *
   * Chrome like the two above it, and excluded from the two-tier rule in
   * section 3.6 for the same reason: nothing nests inside a header, so it
   * cannot produce the stacking that rule exists to prevent.
   *
   * It is its own token rather than an alias of `surface` because the two
   * themes want different answers. On the light canvas the header IS the
   * canvas continuing upward -- the same tint, with only the divider marking where the
   * page starts. In dark that reading collapses: a canvas-coloured bar hinged
   * to the rail leaves the shell looking assembled from two materials, so the
   * header takes the rail's own value instead and the chrome reads as one
   * continuous frame around the content.
   */
  "surface-header",
  /**
   * Section 1.4.11 forces these apart, and they were one token until measured.
   *
   * `divider` is decorative: a hairline between rows or sections. At 1.31:1 on
   * white it is legal precisely because it carries no information -- remove it
   * and nothing is lost but rhythm.
   *
   * `control-border` outlines an input, a select or a checkbox. That IS a user
   * interface component boundary, so 1.4.11 requires 3:1 and 1.31:1 would have
   * been a failure shipped estate-wide. It measures 3.57:1.
   *
   * The same measurement caps `control-border` the other way: at 3.57:1 it
   * fails 1.4.3, so it can outline a control and must never carry text. The
   * test suite asserts both halves.
   */
  "divider",
  "control-border",
  /** The 1px rule between rail and panel, and above Recent. */
  "border-nav-subtle",
  /**
   * The surface of a form control, and where it goes on focus.
   *
   * The two themes run inverse logics with one pair of tokens: in light a
   * control is white at rest -- white means editable -- and stays white; in
   * dark it rests in the sunken well and lifts to the raised surface on
   * focus. Field and Input read these, so neither component branches on
   * theme.
   */
  "control-surface",
  "control-surface-focus",
  "text",
  "text-muted",
  "text-inverse",
  /**
   * Three weights of navigation text. NavShell's rail labels are real text at
   * 0.75rem, never icon-only and never tooltip-dependent, so even the muted
   * step has to clear 4.5:1 on both nav surfaces -- which is gated below.
   */
  "text-nav",
  "text-nav-secondary",
  "text-nav-muted",
  "action",
  "action-hover",
  "action-foreground",
  /**
   * The focus ring's inner colour. Decoupled from the accent by product
   * decision: OR's crimson accent as a focus ring reads as an error state,
   * and sits a few degrees of hue from breach red. A slate-blue ring is
   * calm, product-neutral, and clears 3:1 (SC 2.4.11 / 1.4.11) on every
   * surface a control can sit on in both themes -- gated below. On the
   * purple nav the two-tone ring's outer canvas-coloured line carries
   * visibility, as it always did.
   */
  "focus",
  /**
   * Five states, three colour tiers -- the OR spec's section 4.3. `degraded`
   * deliberately shares the warning amber: any second amber dark enough to
   * pass 4.5:1 lands within a few degrees of hue from the first, which is
   * indistinguishable, and worse under colour blindness. The two states are
   * told apart by glyph and word (at risk = triangle, degraded = diamond),
   * never by colour -- which is why the shared value is a design decision the
   * tests assert rather than a duplication to clean up.
   */
  "status-compliant",
  "status-warning",
  "status-degraded",
  "status-breach",
  "status-not-assessed",
  "status-compliant-bg",
  "status-warning-bg",
  "status-degraded-bg",
  "status-breach-bg",
  "status-not-assessed-bg",
  /**
   * The label on a breach-coloured fill -- a destructive button, primarily.
   * White in light; in dark the breach red is bright enough that white fails
   * at 2.79:1, so the label inverts to deep purple at 6.56:1. This looks like
   * an inconsistency until measured. It is not.
   */
  "status-breach-foreground",
  /**
   * Chart chrome, per the OR spec's section 16. The neutral series inverts
   * between themes -- brand purple disappears into a dark canvas, so dark
   * runs the slate muted tone with a white endpoint. The gridline sits a tier
   * below the hairline in both themes.
   */
  "chart-neutral",
  "chart-grid"
];
var light = {
  // The tinted canvas -- lilac50 is both the solid fallback and the gradient's
  // top stop. Deliberately not white: white is reserved for state (controls,
  // row hover and selection, floating overlays), and on a canvas that starts
  // this deep, white reads as raised at every scroll position.
  surface: palette.lilac50,
  // White. Everything that floats -- menus, popovers, dialogs -- and every
  // row state. Never a page section, a panel or anything wrapping more than
  // one element; the eslint layer polices that side of the contract.
  "surface-raised": palette.white,
  "surface-sunken": palette.lilac150,
  "canvas-from": palette.lilac50,
  "canvas-mid": palette.lilac75,
  "canvas-to": palette.lilac150,
  "surface-nav": palette.purple900,
  "surface-nav-deep": palette.purple950,
  // The header is the working canvas continuing upward; only the divider
  // separates it from the page below.
  "surface-header": palette.lilac50,
  // Darkened from lilac200 with the canvas tint: 1.32:1 on the canvas and
  // still 1.19:1 at the gradient's darkest stop, where lilac200 vanished.
  divider: palette.lilac225,
  "control-border": palette.lilac400,
  "border-nav-subtle": palette.purpleBrandDivider,
  // White at rest, white on focus: in light, white *means* editable.
  "control-surface": palette.white,
  "control-surface-focus": palette.white,
  text: palette.purple950,
  "text-muted": palette.lilac500,
  "text-inverse": palette.white,
  "text-nav": palette.white,
  "text-nav-secondary": palette.lilac250,
  "text-nav-muted": palette.lilac350,
  // Section 3.7: actions run on purple, not on the accent, so they behave
  // identically in every app.
  action: palette.purple900,
  "action-hover": palette.purple950,
  "action-foreground": palette.white,
  // 4.76:1 on a white control, 4.42:1 on the canvas, 4.01:1 at its foot.
  focus: palette.slate500,
  "status-compliant": palette.green600,
  "status-warning": palette.amber700,
  // Shares the warning amber by design -- glyph and word carry the
  // difference. See the note on SEMANTIC_TOKEN_NAMES.
  "status-degraded": palette.amber700,
  "status-breach": palette.red600,
  "status-not-assessed": palette.grey500,
  "status-compliant-bg": palette.green50,
  "status-warning-bg": palette.amber50,
  "status-degraded-bg": palette.amber50,
  "status-breach-bg": palette.red50,
  "status-not-assessed-bg": palette.lilac50,
  "status-breach-foreground": palette.white,
  "chart-neutral": palette.purple900,
  "chart-grid": palette.lilac100
};
var dark = {
  // Not purple950. See the note on slate900 in palette.ts: the canvas is the
  // one dark surface big enough that brand chroma reads as a colour field, so
  // it goes quiet and the chrome around it carries the brand instead.
  surface: palette.slate900,
  // The raised tier follows the canvas out of the brand hue. It carries every
  // overlay -- menus, popovers, the account menu on the rail -- and a purple
  // card floating on a slate canvas reads as a different app's component.
  "surface-raised": palette.slate800,
  "surface-sunken": palette.slate950,
  // Lit from above: slate900 lifted 0.02 L at the top, settling to the canvas
  // by mid-page. This is most of the difference between "dark theme" and
  // "designed for dark".
  "canvas-from": palette.slate875,
  "canvas-mid": palette.slate900,
  "canvas-to": palette.slate900,
  // The whole estate is purple in dark mode, so the nav drops a further step
  // to stay distinguishable from the canvas beside it.
  "surface-nav": palette.purple975,
  "surface-nav-deep": palette.purple985,
  // Deliberately the rail's value, not the canvas's. Everything the user does
  // not act on -- rail, header -- is then one continuous frame, and the canvas
  // is the only lighter shape on screen.
  "surface-header": palette.purple985,
  // The divider follows the content surfaces it marks, not the chrome. A
  // purple rule on a slate card is the one place the two families actually
  // meet, and the purple lost that meeting at 1.06:1. slate750 is the OR
  // spec's quieter hairline, held at the darkest value that still draws on a
  // raised card.
  divider: palette.slate750,
  // Back in the purple family, per the OR spec -- the form layer carries the
  // brand in dark mode. Lifted from the spec's #7D63B0 until the raised
  // surface clears 3:1; see the note on purple400.
  "control-border": palette.purple400,
  "border-nav-subtle": palette.purpleBrandDividerDark,
  // The inverse of light: rest in the sunken well, lift to the raised surface
  // on focus. Same logic -- the control's surface departs from the canvas in
  // the direction that reads as "yours to edit".
  "control-surface": palette.slate950,
  "control-surface-focus": palette.slate800,
  text: palette.white,
  // Slate-native, per the OR spec: content muted text follows the slate
  // canvas. Purple-tinted muted stays inside the purple nav below.
  "text-muted": palette.slate400,
  "text-inverse": palette.purple950,
  "text-nav": palette.white,
  "text-nav-secondary": palette.lilac250,
  "text-nav-muted": palette.lilac350,
  // Section 3.7 calls for a lightened purple here, because deep purple on a
  // deep purple canvas is invisible. The originally proposed #5B32B0 reaches
  // only 2.15:1 against the dark canvas and 1.76:1 against the raised surface
  // -- both short of the 3:1 that WCAG 2.2 1.4.11 requires for a control to be
  // perceivable against what surrounds it.
  //
  // This value sits in a narrow band: darker fails the fill against the canvas,
  // lighter fails the white label on top of it. It measures 3.53:1 as a fill
  // and 5.05:1 for its label, and both are gated below.
  action: palette.purple500,
  "action-hover": palette.purple600,
  "action-foreground": palette.white,
  // The lighter slate: 6.74:1 on the canvas, 5.52:1 on a card, 7.10:1 in
  // the sunken well where a dark control rests.
  focus: palette.slate400,
  "status-compliant": palette.green400,
  "status-warning": palette.amber400,
  "status-degraded": palette.amber400,
  "status-breach": palette.red400,
  "status-not-assessed": palette.grey400,
  "status-compliant-bg": palette.greenDark,
  "status-warning-bg": palette.amberDark,
  "status-degraded-bg": palette.amberDark,
  "status-breach-bg": palette.redDark,
  "status-not-assessed-bg": palette.greyDark,
  // White fails on the dark breach red at 2.79:1; deep purple clears 6.56:1.
  "status-breach-foreground": palette.purple950,
  // The neutral series inverts: brand purple disappears into a dark canvas.
  "chart-neutral": palette.slate400,
  "chart-grid": palette.slate850
};
var semantic = { light, dark };

// src/typography.ts
var typography = {
  families: {
    sans: "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
    mono: "'Fira Mono', ui-monospace, SFMono-Regular, Menlo, monospace"
  },
  /** The only weights the estate ships. 300 is reserved for metric values;
   *  700 is reserved for table headers. */
  weights: [300, 400, 500, 600, 700],
  /** Hero only. */
  display: {
    size: "clamp(1.75rem, 1.6rem + 0.75vw, 2rem)",
    // 28px -> 32px
    lineHeight: "1.15",
    tracking: "-0.03em",
    weight: 500
  },
  /** Page title. */
  h1: {
    size: "clamp(1.25rem, 1.15rem + 0.7vw, 1.375rem)",
    // 20px -> 22px
    lineHeight: "1.25",
    tracking: "-0.025em",
    weight: 500
  },
  /** Section. */
  h2: {
    size: "1rem",
    // 16px
    lineHeight: "1.4",
    tracking: "-0.015em",
    weight: 500
  },
  /** Sub-section. The one 600-weight heading -- see the module note. */
  h3: {
    size: "0.875rem",
    // 14px
    lineHeight: "1.45",
    tracking: "-0.01em",
    weight: 600
  },
  /**
   * The spec's hierarchy stops at h3. A semantic h4 is still legal HTML, so
   * it renders at h3's size rather than inventing a smaller step -- depth
   * beyond three levels is a structure problem, not a type-scale problem.
   */
  h4: {
    size: "0.875rem",
    lineHeight: "1.45",
    tracking: "-0.01em",
    weight: 600
  },
  body: {
    size: "0.875rem",
    // 14px
    lineHeight: "1.5",
    tracking: "0em",
    weight: 400
  },
  /** Table cells. */
  "body-sm": {
    size: "0.8125rem",
    // 13px
    lineHeight: "1.45",
    tracking: "0em",
    weight: 400
  },
  /** @deprecated Renamed `body-sm` by the OR spec; kept while consumers sweep. */
  "body-small": {
    size: "0.8125rem",
    lineHeight: "1.45",
    tracking: "0em",
    weight: 400
  },
  /** Meta, muted. */
  caption: {
    size: "0.75rem",
    // 12px
    lineHeight: "1.4",
    tracking: "0em",
    weight: 400
  },
  /**
   * Section and metric labels -- the eyebrow. Small, but it passes contrast
   * comfortably and uppercase letterspacing aids legibility at this size. It
   * must never be the only place essential information appears.
   */
  eyebrow: {
    size: "0.6875rem",
    // 11px
    lineHeight: "1.2",
    tracking: "0.12em",
    weight: 600,
    transform: "uppercase"
  },
  /** @deprecated Renamed `eyebrow` by the OR spec; kept while consumers sweep. */
  "micro-label": {
    size: "0.6875rem",
    lineHeight: "1.2",
    tracking: "0.12em",
    weight: 600,
    transform: "uppercase"
  },
  /**
   * Column headers. Always smaller than the cell text and heavier in weight
   * -- never larger and lighter.
   */
  "table-head": {
    size: "0.625rem",
    // 10px
    lineHeight: "1.2",
    tracking: "0.05em",
    weight: 700,
    transform: "uppercase"
  },
  /**
   * NavShell's rail label. Sentence case, not uppercase: an uppercase label is
   * roughly 15% wider and would force the rail wider again. Wraps to two lines
   * rather than truncating -- rail labels are real text.
   */
  "nav-label": {
    size: "0.75rem",
    lineHeight: "1.25",
    tracking: "0em",
    weight: 500
  },
  /**
   * NavShell's Recent parent label -- the page a record was reached through.
   * Uppercase and letterspaced so it reads as metadata beneath the record name
   * without competing with it.
   */
  "nav-meta": {
    size: "0.6875rem",
    lineHeight: "1.2",
    tracking: "0.09em",
    weight: 500,
    transform: "uppercase"
  },
  /** The hero figure. Fluid with `display`, since the two sit together. */
  "metric-lg": {
    size: "clamp(1.75rem, 1.6rem + 0.75vw, 2rem)",
    lineHeight: "1.1",
    tracking: "-0.035em",
    weight: 300,
    numeric: "tabular-nums"
  },
  /** Supporting figures. */
  "metric-md": {
    size: "1.375rem",
    // 22px
    lineHeight: "1.15",
    tracking: "-0.025em",
    weight: 400,
    numeric: "tabular-nums"
  },
  /** Inline values. */
  "metric-sm": {
    size: "1rem",
    // 16px
    lineHeight: "1.2",
    tracking: "0em",
    weight: 500,
    numeric: "tabular-nums"
  },
  /** @deprecated Renamed `metric-lg` by the OR spec; kept while consumers sweep. */
  metric: {
    size: "clamp(1.75rem, 1.6rem + 0.75vw, 2rem)",
    lineHeight: "1.1",
    tracking: "-0.035em",
    weight: 300,
    numeric: "tabular-nums"
  },
  /** Figures, identifiers and reference numbers. */
  mono: {
    size: "0.8125rem",
    lineHeight: "1.45",
    tracking: "0em",
    weight: 400,
    numeric: "tabular-nums",
    family: "mono"
  }
};

// src/spacing.ts
var spacing = {
  base: 4,
  scale: {
    0: "0rem",
    1: "0.25rem",
    // 4px
    2: "0.5rem",
    // 8px -- eyebrow to its content
    3: "0.75rem",
    // 12px -- between siblings in a row
    4: "1rem",
    // 16px
    6: "1.5rem",
    // 24px -- page gutter; margin either side of a hairline
    8: "2rem",
    // 32px -- between major sections
    12: "3rem",
    // 48px
    16: "4rem"
    // 64px
  },
  /** The four permitted vertical gaps, as steps of the scale. */
  verticalGaps: [8, 6, 3, 2]
};

// src/radius.ts
var radius = {
  none: "0rem",
  sm: "0.25rem",
  // 4px -- inputs, checkboxes
  md: "0.375rem",
  // 6px -- buttons, menu items
  lg: "0.5rem",
  // 8px -- cards, dialogs, the standard container
  pill: "9999px"
  // badges and tab groups only
};

// src/elevation.ts
var elevation = {
  none: "none",
  /*
   * Offsets and blurs are in rem, per section 5.2, so elevation scales with a
   * user's font-size preference instead of staying pinned to device pixels
   * while everything around it grows. The single exception is the 1px hairline
   * in `sticky`, which is a rule rather than a shadow.
   */
  /*
   * Shadow ink is purple950, authored in OKLCH like every other colour in the
   * package -- the alpha rides in the same functional notation.
   */
  /** Dropdown menus, popovers, hover cards, comboboxes. */
  overlay: "0 0.25rem 0.75rem -0.125rem oklch(0.2112 0.121 287.9893 / 0.12), 0 0.125rem 0.25rem -0.125rem oklch(0.2112 0.121 287.9893 / 0.08)",
  /** Dialogs and sheets, which sit above a scrim. */
  dialog: "0 1rem 2.5rem -0.5rem oklch(0.2112 0.121 287.9893 / 0.24), 0 0.25rem 0.75rem -0.25rem oklch(0.2112 0.121 287.9893 / 0.12)",
  /**
   * The persistent navigation layer.
   *
   * Cast to the right rather than downward, because the nav is a vertical edge
   * and the page sits beside it, not beneath it. Without this the purple rail
   * meets the near-white canvas at a hard 1px line, and the two read as two
   * flat regions abutting rather than as a page under its chrome.
   *
   * This is still "floating above the page" per section 3.6 -- the nav is
   * fixed and content passes under it. It is not licence for a resting shadow
   * on static content.
   */
  nav: "0.0625rem 0 0.75rem -0.25rem oklch(0.2112 0.121 287.9893 / 0.18), 0.125rem 0 0.375rem -0.25rem oklch(0.2112 0.121 287.9893 / 0.1)",
  /** Sticky table headers and toolbars, once the content scrolls beneath. */
  sticky: "0 1px 0 0 oklch(0.2112 0.121 287.9893 / 0.08), 0 0.125rem 0.375rem -0.125rem oklch(0.2112 0.121 287.9893 / 0.06)"
};

// src/motion.ts
var motion = {
  duration: {
    instant: "80ms",
    fast: "140ms",
    base: "200ms",
    slow: "280ms"
  },
  easing: {
    /** Entering the screen: decelerate. */
    enter: "cubic-bezier(0.16, 1, 0.3, 1)",
    /** Leaving the screen: accelerate. */
    exit: "cubic-bezier(0.4, 0, 1, 1)",
    /** Moving between two on-screen states. */
    move: "cubic-bezier(0.4, 0, 0.2, 1)"
  },
  /**
   * Applied under `prefers-reduced-motion: reduce`. Not zero: a 0.01ms
   * transition still fires transitionend, so components that wait on it -- the
   * Radix presence primitives among them -- keep working.
   */
  reduced: {
    duration: "0.01ms",
    easing: "linear"
  }
};

// src/charts.ts
var categorical = [
  palette.purple900,
  // L 0.013
  palette.lilac400,
  // L 0.244
  palette.purple700,
  // L 0.051
  palette.chartLilac,
  // L 0.514
  palette.lilac500,
  // L 0.124
  palette.lilac200,
  // L 0.751
  palette.purple600,
  // L 0.076
  palette.lilac300
  // L 0.451
];
var charts = {
  categorical,
  /**
   * Section 4.2: "Periods with no data still render a full donut ... the chart
   * primitives need a distinct not-assessed rendering that cannot be mistaken
   * for a scored result." Kept out of the categorical ramp so it can never be
   * handed out as a series colour.
   */
  notAssessed: palette.grey500,
  /** Sequential ramp, for heat maps and intensity fills. */
  sequential: [
    palette.lilac50,
    palette.lilac200,
    palette.lilac300,
    palette.lilac400,
    palette.purple600,
    palette.purple900
  ],
  /** Exposed so consumers and tests can check their own series separation. */
  luminance: relativeLuminance
};

export { SEMANTIC_TOKEN_NAMES, charts, contrastRatio, elevation, hexToRgb, meetsAA, motion, radius, relativeLuminance, semantic, spacing, typography };
//# sourceMappingURL=chunk-V3CQSDOF.js.map
//# sourceMappingURL=chunk-V3CQSDOF.js.map